import { MDText } from 'i18n-react';

import { MinMaxFilters } from 'Components/VehicleList/filters/Filters.helpers';
import { VEHICLE_FUEL_TYPES } from 'Utilities/consts';

import texts from '../../../VehicleListV2.texts.json';
import { LISTING_ATTRIBUTE_OPTIONS, SELLER_TYPE_OPTIONS, VEHICLE_TYPE_OPTIONS } from '../../FilterBarV2.consts';

const LocalT = new MDText(texts);

export interface ChipOption {
	label: string;
	value: string;
}

export interface RangeFilterConfig {
	clearMax?: boolean;
	fromId?: string;
	increment: number;
	max: number;
	min: number;
	toId?: string;
	type: 'range';
}

// Array chip: selection stored as a string array on filterId
export interface ArrayChipFilterConfig {
	filterId: string;
	options: ChipOption[];
	type: 'array';
}

// Boolean chip: single selection mapped to a boolean on filterId
export interface BooleanChipFilterConfig {
	activeValue: string;
	filterId: string;
	invertSelection?: boolean;
	options: ChipOption[];
	type: 'boolean';
}

// Independent booleans: each option maps to its own boolean key in the filter store
export interface IndependentBooleansChipFilterConfig {
	filterId: string;
	options: ChipOption[];
	type: 'independentBooleans';
}

export type ChipFilterConfig = ArrayChipFilterConfig | BooleanChipFilterConfig | IndependentBooleansChipFilterConfig;

export interface ToggleFilterConfig {
	filterId: string;
	type: 'toggle';
}

export interface RadioFilterOption {
	from: Nullable<number>;
	label: string;
	to: Nullable<number>;
	value: string;
}

export interface RadioFilterConfig {
	fromId: string;
	options: RadioFilterOption[];
	toId: string;
	type: 'radio';
}

export type FilterConfig = ChipFilterConfig | RadioFilterConfig | RangeFilterConfig | ToggleFilterConfig;

export interface FilterSectionConfig {
	config: FilterConfig;
	id: string;
	title: string;
}

export const SERVICE_HISTORY_OPTIONS: ChipOption[] = [
	{ label: 'All', value: '' },
	{ label: 'Full', value: 'full' },
	{ label: 'Partial', value: 'partial' },
	{ label: 'None', value: 'no' },
	{ label: 'Not yet due', value: 'not_applicable' },
];

export const EXTERIOR_GRADE_OPTIONS: ChipOption[] = [
	{ label: 'All', value: '' },
	{ label: 'Grade 1', value: '1' },
	{ label: 'Grade 2', value: '2' },
	{ label: 'Grade 3', value: '3' },
	{ label: 'Grade 4', value: '4' },
	{ label: 'Grade 5', value: '5' },
];

export const FUEL_OPTIONS: ChipOption[] = [
	{ label: 'All', value: '' },
	{ label: 'Petrol', value: VEHICLE_FUEL_TYPES.PETROL },
	{ label: 'Diesel', value: VEHICLE_FUEL_TYPES.DIESEL },
	{ label: 'Hybrid', value: VEHICLE_FUEL_TYPES.HYBRID },
	{ label: 'Electric', value: VEHICLE_FUEL_TYPES.ELECTRIC },
	{ label: 'Bi-fuel', value: VEHICLE_FUEL_TYPES.GAS_BI_FUEL },
];

export const TRANSMISSION_OPTIONS: ChipOption[] = [
	{ label: 'All', value: '' },
	{ label: 'Manual', value: 'Manual' },
	{ label: 'Auto', value: 'Automatic' },
	{ label: 'Semi-Auto', value: 'Semi-automatic' },
];

export const COLLECTION_OPTIONS: ChipOption[] = [
	{ label: 'All', value: '' },
	{ label: 'Available now', value: 'available' },
];

export const FINANCE_OPTIONS: ChipOption[] = [
	{ label: 'All', value: '' },
	{ label: 'Vehicles not on finance', value: 'excludeFinance' },
];

// --- Section Configurations ---

export const VEHICLE_TYPE_SECTION: { config: ArrayChipFilterConfig; id: string; title: string } = {
	config: {
		filterId: 'vehicleClass',
		options: VEHICLE_TYPE_OPTIONS,
		type: 'array',
	},
	id: 'vehicleType',
	title: `${LocalT.translate('allFiltersModal.vehicleType')}`,
};

export const FILTER_SECTIONS: FilterSectionConfig[] = [
	{
		config: {
			fromId: 'displayPriceFrom',
			increment: MinMaxFilters.price.INCREMENT,
			max: MinMaxFilters.price.MAX,
			min: MinMaxFilters.price.MIN,
			toId: 'displayPriceTo',
			type: 'range',
		},
		id: 'price',
		title: `${LocalT.translate('allFiltersModal.sections.price')}`,
	},
	{
		config: {
			clearMax: false,
			fromId: 'mileageFrom',
			increment: MinMaxFilters.mileage.INCREMENT,
			max: MinMaxFilters.mileage.MAX,
			min: MinMaxFilters.mileage.MIN,
			toId: 'mileageTo',
			type: 'range',
		},
		id: 'mileage',
		title: `${LocalT.translate('allFiltersModal.sections.mileage')}`,
	},
	{
		config: {
			clearMax: false,
			fromId: 'ageFrom',
			increment: MinMaxFilters.age.INCREMENT,
			max: MinMaxFilters.age.MAX,
			min: MinMaxFilters.age.MIN,
			toId: 'ageTo',
			type: 'range',
		},
		id: 'age',
		title: `${LocalT.translate('allFiltersModal.sections.age')}`,
	},
	{
		config: {
			clearMax: false,
			increment: MinMaxFilters.distance.INCREMENT,
			max: MinMaxFilters.distance.MAX,
			min: MinMaxFilters.distance.MIN,
			toId: 'maxDistance',
			type: 'range',
		},
		id: 'distance',
		title: `${LocalT.translate('allFiltersModal.sections.distance')}`,
	},
	{
		config: {
			fromId: 'previousKeepersCountFrom',
			options: [
				{ from: null, label: `${LocalT.translate('allFiltersModal.sections.previousOwnersOptions.any')}`, to: null, value: 'any' },
				{ from: null, label: `${LocalT.translate('allFiltersModal.sections.previousOwnersOptions.none')}`, to: 0, value: 'none' },
				{ from: null, label: `${LocalT.translate('allFiltersModal.sections.previousOwnersOptions.upTo1')}`, to: 1, value: 'up-to-1' },
				{ from: null, label: `${LocalT.translate('allFiltersModal.sections.previousOwnersOptions.upTo2')}`, to: 2, value: 'up-to-2' },
				{ from: null, label: `${LocalT.translate('allFiltersModal.sections.previousOwnersOptions.upTo3')}`, to: 3, value: 'up-to-3' },
				{ from: 4, label: `${LocalT.translate('allFiltersModal.sections.previousOwnersOptions.fourOrMore')}`, to: null, value: '4-or-more' },
			],
			toId: 'previousKeepersCountTo',
			type: 'radio',
		},
		id: 'previousOwners',
		title: `${LocalT.translate('allFiltersModal.sections.previousOwners')}`,
	},
	{
		config: {
			filterId: 'serviceHistory',
			options: SERVICE_HISTORY_OPTIONS,
			type: 'array',
		},
		id: 'serviceHistory',
		title: `${LocalT.translate('allFiltersModal.sections.serviceHistory')}`,
	},
	{
		config: {
			filterId: 'numericGrade',
			options: EXTERIOR_GRADE_OPTIONS,
			type: 'array',
		},
		id: 'exteriorGrade',
		title: `${LocalT.translate('allFiltersModal.sections.exteriorGrade')}`,
	},
	{
		config: {
			filterId: 'fuel',
			options: FUEL_OPTIONS,
			type: 'array',
		},
		id: 'fuel',
		title: `${LocalT.translate('allFiltersModal.sections.fuel')}`,
	},
	{
		config: {
			filterId: 'transmission',
			options: TRANSMISSION_OPTIONS,
			type: 'array',
		},
		id: 'transmission',
		title: `${LocalT.translate('allFiltersModal.sections.transmission')}`,
	},
	{
		config: {
			filterId: 'sellerType',
			options: SELLER_TYPE_OPTIONS,
			type: 'array',
		},
		id: 'stockSource',
		title: `${LocalT.translate('allFiltersModal.sections.stockSource')}`,
	},
	{
		config: {
			filterId: 'profileFeatures',
			options: LISTING_ATTRIBUTE_OPTIONS,
			type: 'independentBooleans',
		},
		id: 'profileFeatures',
		title: `${LocalT.translate('allFiltersModal.sections.profileFeatures')}`,
	},
	{
		config: {
			filterId: 'seenFilter',
			type: 'toggle',
		},
		id: 'viewed',
		title: `${LocalT.translate('allFiltersModal.sections.viewed')}`,
	},
	{
		config: {
			activeValue: 'excludeFinance',
			filterId: 'excludeVehiclesOnFinance',
			options: FINANCE_OPTIONS,
			type: 'boolean',
		},
		id: 'finance',
		title: `${LocalT.translate('allFiltersModal.sections.finance')}`,
	},
	{
		config: {
			activeValue: 'available',
			filterId: 'isAvailableForCollection',
			options: COLLECTION_OPTIONS,
			type: 'boolean',
		},
		id: 'collection',
		title: `${LocalT.translate('allFiltersModal.sections.collection')}`,
	},
];
